import { WebDashboardContentSpecBlocksInner } from "@coeff/agent-types";
import { AnalyticsEventNameType } from "@coeff/api";
import { useSortable } from "@dnd-kit/sortable";
import React, { useEffect, useRef, useState } from "react";

import {
  AlertExclamation,
  Copy,
  DeleteIcon,
  DownloadIcon,
  PencilIcon,
} from "../../components/Icons";
import { useTrack } from "../../utils";

import { BlockEditPopover, BlockEditUpdates } from "./BlockEditPopover";
import { DashboardWindow, getSessionQuickFilter } from "./dashboardHelpers";
import { TextBlockEditPopover } from "./TextBlockEditPopover";
import { useIsMobile } from "./useIsMobile";
import { useWebDashboardViewerContext } from "./WebDashboardViewerContext";

/**
 * Hover control that is just its glyph — no circle, border, or fill. 32px box so the hit
 * target stays the same size as the `themed-icon-button` it replaces.
 */
const PLAIN_ACTION_CLASS =
  "flex items-center justify-center w-8 h-8 cursor-pointer text-main-muted-foreground " +
  "hover:text-main-foreground transition-colors";

export enum InsertPosition {
  Before = "before",
  After = "after",
  Above = "above",
  Below = "below",
}

interface Props {
  id: string;
  children: React.ReactNode;
  style?: React.CSSProperties;
  /** Additional classes merged onto the root div (e.g. mobile sizing overrides). */
  className?: string;
  index: number;
  activeIndex: number;
  canEdit: boolean;
  onDelete: (id: string) => void;
  /** Open the chat sidebar and explain this block (instant if a stored explanation exists). */
  onExplainInChat?: (id: string) => void;
  onEditBlock?: (blockId: string, updates: BlockEditUpdates) => void;
  /** Cache-only write for the popover's controls; saved by onCommitBlockEdits. */
  onApplyBlockEditLocally?: (blockId: string, updates: BlockEditUpdates) => void;
  /** Ends the block's popover session: one save for everything applied locally. */
  onCommitBlockEdits?: (blockId: string) => void;
  // Drop direction calculated by parent based on cursor position
  dropDirection?: InsertPosition | null;
  targetWindow: DashboardWindow;
  block: WebDashboardContentSpecBlocksInner;
}

export const DraggableBlock: React.FC<Props> = ({
  id,
  children,
  style: externalStyle,
  className: externalClassName,
  index,
  activeIndex,
  canEdit = false,
  onDelete,
  onExplainInChat,
  onEditBlock,
  onApplyBlockEditLocally,
  onCommitBlockEdits,
  dropDirection,
  targetWindow,
  block,
}) => {
  const track = useTrack();
  const { dashboardId, dashboardName, spreadsheetId, isAiThinking, isArchived } =
    useWebDashboardViewerContext();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isEditPopoverOpen, setIsEditPopoverOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const searchOpenedByUserRef = useRef(false);
  const quickFilterTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const searchRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const { attributes, listeners, setNodeRef, transition, isDragging, over } = useSortable({
    id,
    disabled: !canEdit || isAiThinking,
  });

  useEffect(() => {
    if (isAiThinking) {
      setIsMenuOpen(false);
      setIsEditPopoverOpen(false);
    }
  }, [isAiThinking]);

  // Cancel a pending (debounced) quick-filter push on unmount.
  useEffect(
    () => () => {
      if (quickFilterTimerRef.current) {
        clearTimeout(quickFilterTimerRef.current);
      }
    },
    []
  );

  // Re-hydrate the search chrome from the session quick-filter memory: the grid
  // re-seeds its filter from the same memory on creation, so after a remount
  // (navigate away and back) the rows would be filtered while this box sat
  // empty and collapsed — invisible and hard to clear.
  useEffect(() => {
    const remembered = getSessionQuickFilter(block.block_id);
    if (remembered) {
      setSearchValue(remembered);
      setIsSearchOpen(true);
    }
    // Mount-only by design: afterwards this component's own state is the source
    // of truth and pushes into the grid via setQuickFilter.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isMenuOpen) {
      return;
    }

    const handleClickOutside = (e: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(e.target as Node) &&
        menuButtonRef.current &&
        !menuButtonRef.current.contains(e.target as Node)
      ) {
        setIsMenuOpen(false);
      }
    };

    targetWindow.document.addEventListener("click", handleClickOutside);

    return () => targetWindow.document.removeEventListener("click", handleClickOutside);
  }, [isMenuOpen, targetWindow]);

  const handleExplainClick = () => {
    setIsMenuOpen(false);

    // Get block title (metric cards use 'label', others use 'title')
    const blockTitle = block.type === "metric_card" ? (block as any).label : (block as any).title;
    track(AnalyticsEventNameType.VibeDashExplainClicked, {
      vibe_dash_id: dashboardId,
      vibe_dash_block_id: block.block_id,
      vibe_dash_block_type: block.type,
      vibe_dash_block_title: blockTitle,
      ss_id: spreadsheetId,
      vibe_dash_name: dashboardName,
    });

    onExplainInChat?.(id);
  };

  const handleCopyClick = async e => {
    e.preventDefault();
    e.stopPropagation();
    if (isAiThinking) {
      return;
    }
    setIsMenuOpen(false);
    const api = getDataTableExportApi();
    await api?.copyCsvToClipboard?.();
  };

  // Determine insert position when this block is being hovered over
  let insertPosition: InsertPosition | undefined;

  if (over?.id === id && !isDragging) {
    // If parent provided a specific drop direction based on cursor position, use it
    if (dropDirection) {
      insertPosition = dropDirection;
    } else {
      // Fallback to original logic for horizontal positioning
      if (activeIndex === -1) {
        // Dragging from another row - show Before indicator
        insertPosition = InsertPosition.Before;
      } else if (index !== activeIndex) {
        // Dragging within same row
        insertPosition = index > activeIndex ? InsertPosition.After : InsertPosition.Before;
      }
    }
  }

  const style: React.CSSProperties = {
    transition,
    opacity: isDragging ? 0.5 : 1,
    position: "relative",
    // While this block's edit popover is open, ring the block in the theme's primary color
    // so the popover stays visually paired with the block it edits — the popover can land
    // beside or over a NEIGHBORING block (see usePopoverPosition), where the pairing would
    // otherwise be ambiguous.
    ...(isEditPopoverOpen
      ? {
          boxShadow: "0 0 0 2px var(--theme--color-primary-button-background, #3b82f6)",
          borderRadius: "var(--radius-themed, 12px)",
        }
      : {}),
    ...externalStyle,
  };

  const isDataTableBlock = block.type === "data_table";
  // Section headers are chrome, not a card: per the Figma their hover controls are bare
  // pencil/trash glyphs sitting on the page, not the circular buttons, and delete is one click
  // rather than an overflow menu. Text blocks are cards, so they keep the card treatment.
  const usesPlainBlockActions = block.type === "section_header";
  const isMobile = useIsMobile(targetWindow);
  // On mobile, all cards hide the entire top-right action cluster (edit +
  // options), so tapping the corner does nothing.
  const hideBlockActions = isMobile;

  // The action overlay stays visible (not just on hover) while any of these are
  // open — mirror that state as a class so the table's row-count label can fade
  // out from under it (.coeff-data-table-row-count rules in WebDashboardContent).
  // Gated on the overlay actually rendering: on mobile a rehydrated quick-filter
  // sets isSearchOpen with no overlay, and the count must stay visible there.
  const actionsPinnedOpen = !hideBlockActions && (isMenuOpen || isEditPopoverOpen || isSearchOpen);

  const getDataTableExportApi = (): {
    copyCsvToClipboard?: () => Promise<void>;
    downloadCsv?: () => void;
    setQuickFilter?: (text: string) => void;
  } | null => {
    try {
      const cardEl = targetWindow.document.getElementById(`block_${block.block_id}`) as
        | (HTMLElement & {
            __coeffDataTableExportApi?: {
              copyCsvToClipboard?: () => Promise<void>;
              downloadCsv?: () => void;
              setQuickFilter?: (text: string) => void;
            };
          })
        | null;

      if (cardEl?.__coeffDataTableExportApi) {
        return cardEl.__coeffDataTableExportApi;
      }

      const containerEl = cardEl?.querySelector(".data-table-container") as
        | (HTMLElement & {
            __coeffDataTableExportApi?: {
              copyCsvToClipboard?: () => Promise<void>;
              downloadCsv?: () => void;
              setQuickFilter?: (text: string) => void;
            };
          })
        | null;
      return containerEl?.__coeffDataTableExportApi ?? null;
    } catch {
      return null;
    }
  };

  // Push the filter to the grid on a 200ms debounce — the same cadence as the
  // in-table search used on mobile and in drill-down modals, so filtering feels
  // identical across platforms (and large tables aren't re-filtered per
  // keystroke). The visible input updates immediately; clearing skips the
  // debounce so an emptied box never shows stale filtered rows.
  const handleSearchChange = (value: string) => {
    setSearchValue(value);
    if (quickFilterTimerRef.current) {
      clearTimeout(quickFilterTimerRef.current);
      quickFilterTimerRef.current = null;
    }
    if (value === "") {
      getDataTableExportApi()?.setQuickFilter?.("");
      return;
    }
    quickFilterTimerRef.current = setTimeout(() => {
      quickFilterTimerRef.current = null;
      getDataTableExportApi()?.setQuickFilter?.(value);
    }, 200);
  };

  // Collapse the search box on click-outside, but only when it's empty — an
  // active filter keeps it open so the user can see/clear what they typed.
  useEffect(() => {
    if (!isSearchOpen) {
      return;
    }
    const handleClickOutside = (e: MouseEvent) => {
      if (
        searchRef.current &&
        !searchRef.current.contains(e.target as Node) &&
        searchValue === ""
      ) {
        setIsSearchOpen(false);
      }
    };
    targetWindow.document.addEventListener("click", handleClickOutside);
    return () => targetWindow.document.removeEventListener("click", handleClickOutside);
  }, [isSearchOpen, searchValue, targetWindow]);

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`group/block${actionsPinnedOpen ? " coeff-block-actions-pinned" : ""}${
        externalClassName ? ` ${externalClassName}` : ""
      }`}
      data-block-id={id}
    >
      {/* Insert indicator line - before (left) */}
      {insertPosition === InsertPosition.Before && (
        <div className="absolute -left-[10px] top-0 bottom-0 w-[3px] bg-blue-500 rounded-full z-20" />
      )}

      {/* Insert indicator line - after (right) */}
      {insertPosition === InsertPosition.After && (
        <div className="absolute -right-[10px] top-0 bottom-0 w-[3px] bg-blue-500 rounded-full z-20" />
      )}

      {/* Insert indicator line - above (top) */}
      {insertPosition === InsertPosition.Above && (
        <div className="absolute left-0 right-0 -top-[10px] h-[3px] bg-blue-500 rounded-full z-20" />
      )}

      {/* Insert indicator line - below (bottom) */}
      {insertPosition === InsertPosition.Below && (
        <div className="absolute left-0 right-0 -bottom-[10px] h-[3px] bg-blue-500 rounded-full z-20" />
      )}

      {/* Drag and delete buttons are z-11 so they are above above the resize indicators.
          Not rendered on mobile: `opacity-0` until hover leaves the handle permanently invisible on
          a touch device yet still fully hit-testable, so blocks could be dragged and reordered by
          pressing apparently-empty space. Reordering is desktop-only, and unmounting the button
          also removes the sole dnd-kit drag activator (`listeners` are spread nowhere else). */}
      {canEdit && !isMobile && (
        <div className="absolute -top-2 -left-2 z-11 opacity-0 group-hover/block:opacity-100 transition-opacity duration-150">
          <div className="relative inline-block group/drag-handle">
            <button
              {...attributes}
              {...(isAiThinking ? {} : listeners)}
              className={`themed-icon-button ${
                isAiThinking
                  ? "opacity-50 cursor-not-allowed"
                  : "cursor-grab active:cursor-grabbing"
              }`}
              aria-label={
                isAiThinking ? "Cannot move block while AI is thinking" : "Drag to reorder"
              }
              disabled={isAiThinking}
            >
              <span className="material-icons text-main-muted-foreground !text-xl">
                drag_indicator
              </span>
            </button>

            {/* Only show the tooltip when AI is not currently updating the block */}
            {!isAiThinking && (
              <div className="pointer-events-none absolute left-full ml-2 top-1/2 -translate-y-1/2 z-20 px-2 py-1 rounded bg-black/80 text-white text-[11px] shadow-sm whitespace-nowrap opacity-0 group-hover/drag-handle:opacity-100">
                Drag to reorder
              </div>
            )}
          </div>
        </div>
      )}

      {/* Top-right action icons — inside the card. Hidden entirely on mobile (tapping the
          corner does nothing). On archived (read-only) dashboards the edit button and
          triple-dot menu are hidden (gated individually below), but the read-only table
          search stays available so archived tables remain filterable. */}
      {!hideBlockActions && (isDataTableBlock || (!isArchived && (canEdit || onExplainInChat))) && (
        <div
          className={`absolute top-2 right-2 transition-opacity duration-150 ${
            isEditPopoverOpen ? "z-50" : "z-10"
          } ${
            isMenuOpen || isEditPopoverOpen || isSearchOpen
              ? "opacity-100"
              : "opacity-0 group-hover/block:opacity-100"
          }`}
        >
          <div className="flex items-center gap-1">
            {/* Search is just another action: a collapsed icon that expands inline,
                sitting alongside edit/options rather than floating over the table. */}
            {isDataTableBlock && (
              <div ref={searchRef} className="flex items-center">
                {isSearchOpen ? (
                  <div className="relative flex items-center">
                    <span className="material-icons pointer-events-none absolute left-2 !text-lg text-main-muted-foreground">
                      search
                    </span>
                    <input
                      ref={searchInputRef}
                      type="text"
                      name="data-table-search-input"
                      placeholder="Search"
                      value={searchValue}
                      autoFocus={searchOpenedByUserRef.current}
                      onChange={e => handleSearchChange(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === "Escape") {
                          handleSearchChange("");
                          setIsSearchOpen(false);
                        }
                      }}
                      className="h-8 w-40 rounded-full border border-main-border bg-main-background
                        pl-8 pr-8 text-sm text-main-foreground
                        focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-button-background"
                    />
                    <button
                      type="button"
                      aria-label="Clear search"
                      onClick={() => {
                        handleSearchChange("");
                        setIsSearchOpen(false);
                      }}
                      className="absolute right-1.5 flex items-center justify-center text-main-muted-foreground hover:text-main-foreground"
                    >
                      <span className="material-icons !text-lg">close</span>
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => {
                      searchOpenedByUserRef.current = true;
                      setIsSearchOpen(true);
                    }}
                    className="themed-icon-button"
                    title="Search table"
                  >
                    <span className="material-icons text-main-muted-foreground !text-xl">
                      search
                    </span>
                  </button>
                )}
              </div>
            )}
            {!isArchived && canEdit && onEditBlock && (
              <button
                onClick={() => {
                  if (isAiThinking) {
                    return;
                  }
                  setIsMenuOpen(false);
                  const willOpen = !isEditPopoverOpen;
                  setIsEditPopoverOpen(willOpen);
                  if (willOpen) {
                    track(AnalyticsEventNameType.VibeDashBlockEditOpened, {
                      vibe_dash_id: dashboardId,
                      ss_id: spreadsheetId,
                      vibe_dash_name: dashboardName,
                      vibe_dash_block_id: block.block_id,
                      vibe_dash_block_type: block.type,
                    });
                  }
                }}
                className={`${usesPlainBlockActions ? PLAIN_ACTION_CLASS : "themed-icon-button"} ${
                  isAiThinking ? "opacity-50 cursor-not-allowed" : ""
                }`}
                title={isAiThinking ? "Cannot edit block while AI is thinking" : "Edit block"}
                disabled={isAiThinking}
              >
                {usesPlainBlockActions ? (
                  <PencilIcon width={16} height={16} />
                ) : (
                  <span className="material-icons text-main-muted-foreground !text-xl">edit</span>
                )}
              </button>
            )}

            {usesPlainBlockActions
              ? !isArchived &&
                canEdit && (
                  <button
                    onClick={e => {
                      e.stopPropagation();
                      if (!isAiThinking) {
                        onDelete(id);
                      }
                    }}
                    className={`${PLAIN_ACTION_CLASS} ${
                      isAiThinking ? "opacity-50 cursor-not-allowed" : ""
                    }`}
                    aria-label={
                      isAiThinking ? "Cannot delete block while AI is thinking" : "Delete block"
                    }
                    disabled={isAiThinking}
                  >
                    <DeleteIcon width={16} height={16} />
                  </button>
                )
              : !isArchived &&
                (canEdit || onExplainInChat || isDataTableBlock) && (
                  <button
                    ref={menuButtonRef}
                    onClick={() => {
                      if (isAiThinking) {
                        return;
                      }
                      setIsEditPopoverOpen(false);
                      setIsMenuOpen(!isMenuOpen);
                    }}
                    className={`themed-icon-button ${
                      isAiThinking ? "opacity-50 cursor-not-allowed" : ""
                    }`}
                    title={
                      isAiThinking ? "Cannot access options while AI is thinking" : "Block options"
                    }
                    disabled={isAiThinking}
                  >
                    <span className="material-icons text-main-muted-foreground !text-xl">
                      more_vert
                    </span>
                  </button>
                )}
          </div>

          {isMenuOpen && (
            <div ref={menuRef} className="themed-dropdown-menu mt-1 w-max z-10">
              {onExplainInChat && (
                <button onClick={handleExplainClick} className="themed-dropdown-item px-3 py-2">
                  <AlertExclamation fill="currentColor" className="shrink-0" />
                  <span className="text-sm font-semibold whitespace-nowrap leading-[18px]">
                    Explain
                  </span>
                </button>
              )}
              {isDataTableBlock && (
                <>
                  <button
                    onClick={handleCopyClick}
                    className={`themed-dropdown-item px-3 py-2 ${
                      isAiThinking ? "opacity-50 cursor-not-allowed" : ""
                    }`}
                    title={isAiThinking ? "Cannot copy while AI is thinking" : "Copy"}
                    disabled={isAiThinking}
                  >
                    <Copy fill="currentColor" className="shrink-0" />
                    <span className="text-sm font-semibold whitespace-nowrap leading-[18px]">
                      Copy
                    </span>
                  </button>
                  <button
                    onClick={e => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (isAiThinking) {
                        return;
                      }
                      setIsMenuOpen(false);
                      const api = getDataTableExportApi();
                      api?.downloadCsv?.();
                    }}
                    className="themed-dropdown-item px-3 py-2"
                    title={isAiThinking ? "Cannot download while AI is thinking" : "Download CSV"}
                    disabled={isAiThinking}
                  >
                    <DownloadIcon fill="currentColor" className="shrink-0" />
                    <span className="text-sm font-semibold whitespace-nowrap leading-[18px]">
                      Download CSV
                    </span>
                  </button>
                </>
              )}
              {canEdit && (
                <button
                  onClick={e => {
                    e.stopPropagation();
                    if (!isAiThinking) {
                      setIsMenuOpen(false);
                      onDelete(id);
                    }
                  }}
                  className="themed-dropdown-item px-3 py-2"
                  aria-label={
                    isAiThinking ? "Cannot delete block while AI is thinking" : "Delete block"
                  }
                  disabled={isAiThinking}
                >
                  <DeleteIcon fill="currentColor" className="shrink-0" />
                  <span className="text-sm font-semibold whitespace-nowrap leading-[18px]">
                    Delete
                  </span>
                </button>
              )}
            </div>
          )}

          {isEditPopoverOpen &&
            onEditBlock &&
            (block.type === "text_block" ? (
              <TextBlockEditPopover
                block={block as WebDashboardContentSpecBlocksInner & { type: "text_block" }}
                onSave={onEditBlock}
                onClose={() => setIsEditPopoverOpen(false)}
                targetWindow={targetWindow}
              />
            ) : (
              <BlockEditPopover
                block={block}
                onSave={onEditBlock}
                // Without a session provider (no viewer wiring), fall back to saving each
                // control change through onEditBlock directly, with nothing to commit.
                onApplyLocal={onApplyBlockEditLocally ?? onEditBlock}
                onCommit={onCommitBlockEdits ?? (() => {})}
                // Dismissal saves: the block already shows the edits, so discarding them
                // on an outside click would read as the tool undoing the user's work.
                onClose={() => {
                  onCommitBlockEdits?.(block.block_id);
                  setIsEditPopoverOpen(false);
                }}
                targetWindow={targetWindow}
              />
            ))}
        </div>
      )}
      {canEdit && isAiThinking && (
        <div className="pointer-events-none absolute top-0 left-0 mt-1 ml-2 z-20 px-2 py-1 rounded bg-black/80 text-white text-[11px] shadow-sm opacity-0 group-hover/block:opacity-100">
          AI is currently updating the dashboard. You can&apos;t move or delete this block right
          now.
        </div>
      )}

      {children}
    </div>
  );
};
